/**
 * QC-REVIEW-UI-COMPACT-002 (Oct 2026) — compact QC Review command bar.
 * Replaces the oversized green "QC Review Mode" panel. Metadata + View Notes +
 * the unchanged QCInlineActions (compact mode). Existing data-testids preserved.
 */
import React, { useState } from 'react';
import { ClipboardCheck, Info, UserRound, CalendarDays, Clock, AlertTriangle, CheckCircle2, MessageSquare } from 'lucide-react';
import QCInlineActions from './QCInlineActions';
import QCNotesDrawer, { useQcTimeline } from './QCNotesDrawer';
import TypingReturnToQcBanner from './TypingReturnToQcBanner';
import { QcAddDocumentButton } from './QcAddDocument';
import { qcComponentOf, COMPONENT_LABELS } from '../../lib/qcComponent';

const Sep = () => <span className="hidden md:block h-5 w-px bg-slate-200" aria-hidden="true" />;

const Meta = ({ icon: Icon, children, title, testId }) => (
  <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600 whitespace-nowrap" title={title || undefined} data-testid={testId}>
    <Icon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
    {children}
  </span>
);

const QC_NOTE_ROLES = ['qc_specialist', 'admin', 'tax_search_team_leader', 'title_search_team_leader'];

const QCReviewBar = ({ orderData, user, onOrderUpdate, formatRelativeAgo, formatDateTime, showPreviewPdf = false, canAddDocument = false }) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const { entries, loading, addNote } = useQcTimeline(orderData);

  // MLS-COMPONENT-QC-001: component-level item → submitter/time come from the component under review
  const qcComp = qcComponentOf(orderData);
  const compDoc = qcComp ? (orderData?.components?.[qcComp] || {}) : null;
  const submittedByName = (compDoc ? compDoc.qc_submitted_by_name : null) || orderData?.qc_submitted_by_name || orderData?.qc_submitted_by_full_name || null;
  const submittedAt = (compDoc ? compDoc.qc_submitted_at : null) || orderData?.qc_submitted_at || null;
  const modifiedAt = orderData?.updated_at || null;
  const tsr = orderData?.tax_sheet_review || {};
  const selfReviewed = tsr?.status === 'reviewed' && !!tsr?.reviewed_by_name;
  const canAddNote = QC_NOTE_ROLES.includes(user?.role);
  const returnedFromTyping = orderData?.status === 'qc_review' && orderData?.typing_status === 'returned_to_qc';
  const addDocBtn = canAddDocument
    ? <QcAddDocumentButton orderId={orderData.id} onUploaded={() => onOrderUpdate?.()} compact />
    : null;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_6px_24px_-12px_rgba(15,23,42,0.25)] px-4 py-2.5" data-testid="qc-specialist-banner" data-compact="true">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* Identity */}
          <div className="flex items-center gap-2.5 mr-1">
            <span className="h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-sm shadow-emerald-500/40 ring-4 ring-emerald-50 transition-transform duration-200 hover:scale-105">
              <ClipboardCheck className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="text-[15px] font-bold text-slate-900" data-testid="qc-bar-title">{qcComp ? `QC Review — ${COMPONENT_LABELS[qcComp]} component` : 'QC Review'}</span>
            <span title="You are reviewing this order. Verify all details before approving or returning." className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="qc-review-info">
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </div>
          <Sep />

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="qc-review-metadata-strip">
            <Meta icon={UserRound} testId="qc-meta-submitted-by">{submittedByName || '—'}</Meta>
            <Sep />
            <Meta icon={CalendarDays} testId="qc-meta-submitted-at" title={submittedAt ? formatDateTime(submittedAt) : undefined}>
              Submitted {formatRelativeAgo(submittedAt) || '—'}
            </Meta>
            <Sep />
            <Meta icon={Clock} testId="qc-meta-last-modified" title={modifiedAt ? formatDateTime(modifiedAt) : undefined}>
              Modified {formatRelativeAgo(modifiedAt) || '—'}
            </Meta>
            <span
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-semibold whitespace-nowrap transition-colors duration-200 ${
                selfReviewed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
              title={selfReviewed && tsr?.reviewed_at ? formatDateTime(tsr.reviewed_at) : undefined}
              data-testid="qc-meta-self-review" data-state={selfReviewed ? 'reviewed' : 'pending'}
            >
              {selfReviewed ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> : <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />}
              {selfReviewed ? `Self-review ✓ ${tsr.reviewed_by_name}` : 'Self-review pending'}
            </span>
          </div>

          {/* Actions — right aligned, wrap to a second row on laptops */}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {!returnedFromTyping && addDocBtn}
            <button type="button" onClick={() => setNotesOpen(true)} data-testid="qc-view-notes-btn" aria-haspopup="dialog" aria-expanded={notesOpen}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 text-[#4f46e5] text-[13px] font-semibold
                transition-[background-color,border-color,box-shadow,transform] duration-200 hover:bg-violet-100 hover:border-violet-300 hover:shadow hover:-translate-y-px">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              View Notes <span data-testid="qc-view-notes-count">({loading ? '…' : entries.length})</span>
            </button>
            <QCInlineActions orderData={orderData} currentUser={user} onOrderUpdate={onOrderUpdate} showPreviewPdf={showPreviewPdf} compact />
          </div>
        </div>
        {/* QC-REVIEW-COMPACT-002 — one compact alert row, only when Typing returned the order */}
        <TypingReturnToQcBanner
          order={orderData}
          actions={(
            <>
              <button type="button" onClick={() => setNotesOpen(true)} data-testid="typing-return-view-details-btn"
                className="h-8 px-2.5 inline-flex items-center rounded-lg text-[12.5px] font-semibold text-[#4f46e5] transition-[background-color,transform] duration-200 hover:bg-violet-50 hover:-translate-y-px">
                View Details
              </button>
              {addDocBtn}
            </>
          )}
        />
      </div>

      <QCNotesDrawer open={notesOpen} onClose={() => setNotesOpen(false)} orderNumber={orderData?.order_number}
        entries={entries} loading={loading} canAddNote={canAddNote} onAddNote={addNote} />
    </>
  );
};

export default QCReviewBar;
